import React, { FunctionComponent } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

const INPUT_VARIANTS = ['filled', 'outlined', 'standard'] as const;
type InputVariant = typeof INPUT_VARIANTS[number];

const INPUT_SIZES = ['small', 'medium'] as const;
type InputSize = typeof INPUT_SIZES[number];

const INPUT_TYPES = [
  'text',
  'password',
  'number',
  'email',
  'tel',
  'url',
  'search',
  'date',
  'time',
  'datetime-local'
] as const;
type InputType = typeof INPUT_TYPES[number];

interface InputProps extends Omit<TextFieldProps, 'variant' | 'size' | 'type'> {
  label?: string;
  value: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: InputType;
  variant?: InputVariant;
  size?: InputSize;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  max?: number;
  min?: number;
}

export const Input: FunctionComponent<InputProps> = React.memo(({
  label,
  value,
  onChange,
  type = 'text',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  error = false,
  helperText,
  disabled = false,
  className = '',
  maxLength,
  minLength,
  required = false,
  placeholder,
  autoFocus = false,
  max,
  min,
  slotProps,
  ...props
}) => {
  const ErrorFallback: React.FC = () => (
    <TextField
      error
      variant="outlined"
      size="medium"
      fullWidth
      disabled
      value=""
      helperText="Error rendering input"
      className={className}
    />
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const numValue = Number(event.target.value);
      if (typeof max === 'number' && numValue > max) return;
      if (typeof min === 'number' && numValue < min) return;
    }
    onChange(event);
  };

  try {
    return (
      <TextField
        label={label}
        value={value}
        onChange={handleChange}
        type={type}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        error={error}
        helperText={helperText}
        disabled={disabled}
        className={className}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        slotProps={slotProps}
        {...props}
      />
    );
  } catch (error) {
    console.error('Input rendering error:', error);
    return <ErrorFallback />;
  }
});

Input.displayName = 'Input';

export type { InputProps, InputVariant, InputSize, InputType };